import type { ContentCardItem } from '@content-search/domain';

import * as styles from './ContentCard.css';
import { FullSizeImageDisplay } from './FullSizeImageDisplay';
import { PolicyDisplay } from './PolicyDisplay';

interface ExpandableDetailsProps {
  item: ContentCardItem;
  isExpanded: boolean;
  isContentBlurred: boolean;
  onToggleBlur?: () => void;
}

/**
 * Component for expandable details section
 */
// eslint-disable-next-line complexity
export function ExpandableDetails({ item, isExpanded, isContentBlurred, onToggleBlur }: ExpandableDetailsProps) {
  const detailsClassName = `${styles.expandableDetails} ${isExpanded ? styles.expandableDetailsVisible : styles.expandableDetailsHidden}`;

  // Prepare columns to display
  const columns = [];

  // General Information Column
  columns.push(
    <div key="general-info" className={styles.detailSection}>
      <h4 className={styles.detailSectionTitle}>Content Information</h4>
      <div className={styles.detailContent}>
        <div className={styles.metadataRow}>
          <span className={styles.metadataLabel}>Subject ID:</span>
          <span className={styles.metadataValue}>{item.subjectId}</span>
        </div>
        {item.timestamp && (
          <div className={styles.metadataRow}>
            <span className={styles.metadataLabel}>Timestamp:</span>
            <span className={styles.metadataValue}>{new Date(item.timestamp).toLocaleString()}</span>
          </div>
        )}
        {item.eventType && (
          <div className={styles.metadataRow}>
            <span className={styles.metadataLabel}>Event Type:</span>
            <span className={styles.metadataValue}>{item.eventType}</span>
          </div>
        )}
        {item.contentType && (
          <div className={styles.metadataRow}>
            <span className={styles.metadataLabel}>Content Type:</span>
            <span className={styles.metadataValue}>{item.contentType}</span>
          </div>
        )}
        {item.topicName && (
          <div className={styles.metadataRow}>
            <span className={styles.metadataLabel}>Topic Name:</span>
            <span className={styles.metadataValue}>{item.topicName}</span>
          </div>
        )}
        {(item.contentUrl || item.audioFileUrl) && (
          <div className={styles.metadataRow}>
            <span className={styles.metadataLabel}>Content URL:</span>
            <span className={styles.metadataValue}>{item.contentUrl || item.audioFileUrl}</span>
          </div>
        )}
      </div>
    </div>
  );

  // Classification Results Column
  if (item.samplingStatus === 'sampled') {
    columns.push(
      <div key="classification" className={styles.detailSection}>
        <h4 className={styles.detailSectionTitle}>Classification Results</h4>
        <div className={styles.detailContent}>
          {item.language && (
            <div className={styles.metadataRow}>
              <span className={styles.metadataLabel}>Language:</span>
              <span className={styles.metadataValue}>{item.language}</span>
            </div>
          )}
          {item.classificationMetadata?.confidence_score !== undefined && (
            <div className={styles.metadataRow}>
              <span className={styles.metadataLabel}>Confidence:</span>
              <span className={styles.metadataValue}>
                {item.classificationMetadata.confidence_score.toFixed(2)} ({Math.round(item.classificationMetadata.confidence_score * 100)}%)
              </span>
            </div>
          )}
          {item.classificationResult && (
            <div className={styles.metadataRow}>
              <span className={styles.metadataLabel}>Classification:</span>
              <span className={styles.metadataValue}>{item.classificationResult}</span>
            </div>
          )}
          {item.classificationMetadata?.processing_time_ms !== undefined && (
            <div className={styles.metadataRow}>
              <span className={styles.metadataLabel}>Processing Time:</span>
              <span className={styles.metadataValue}>{item.classificationMetadata.processing_time_ms}ms</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Policy Information Column
  const hasPolicyResult = item.policyResult && item.policyResult.totalPoliciesEvaluated > 0;

  if (hasPolicyResult) {
    columns.push(
      <div key="policy-info" className={styles.detailSection}>
        <h4 className={styles.detailSectionTitle}>Policy Information</h4>
        <div className={styles.detailContent}>{item.policyResult && <PolicyDisplay policyResult={item.policyResult} />}</div>
      </div>
    );
  }

  // Metadata Column
  const hasMetadata = Object.keys(item.metadata).filter(key => key !== '__typename').length > 0;

  if (hasMetadata) {
    columns.push(
      <div key="metadata" className={styles.detailSection}>
        <h4 className={styles.detailSectionTitle}>Metadata</h4>
        <div className={styles.detailContent}>
          {Object.entries(item.metadata)
            .filter(([key]) => key !== '__typename')
            .map(([key, value]) => (
              <div key={key} className={styles.metadataRow}>
                <span className={styles.metadataLabel}>{key}:</span>
                <span className={styles.metadataValue}>{value}</span>
              </div>
            ))}
        </div>
      </div>
    );
  }

  // User Information Column
  const hasUserInfo = item.subjectOwner;

  if (hasUserInfo) {
    columns.push(
      <div key="user-info" className={styles.detailSection}>
        <h4 className={styles.detailSectionTitle}>User Information</h4>
        <div className={styles.detailContent}>
          {item.subjectOwner && item.subjectOwner.type && (
            <div className={styles.metadataRow}>
              <span className={styles.metadataLabel}>Type:</span>
              <span className={styles.metadataValue}>{item.subjectOwner.type}</span>
            </div>
          )}
          {item.subjectOwner && item.subjectOwner.userId && (
            <div className={styles.metadataRow}>
              <span className={styles.metadataLabel}>User ID:</span>
              <span className={styles.metadataValue}>{item.subjectOwner.userId}</span>
            </div>
          )}
          {item.subjectOwner && item.subjectOwner.username && (
            <div className={styles.metadataRow}>
              <span className={styles.metadataLabel}>Username:</span>
              <span className={styles.metadataValue}>{item.subjectOwner.username}</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Custom Fields Column
  const hasCustomFields = item.customFields && Object.keys(item.customFields).length > 0;

  if (hasCustomFields) {
    columns.push(
      <div key="custom-fields" className={styles.detailSection}>
        <h4 className={styles.detailSectionTitle}>Custom Fields</h4>
        <div className={styles.detailContent}>
          {item.customFields &&
            Object.entries(item.customFields).map(([key, value]) => (
              <div key={key} className={styles.metadataRow}>
                <span className={styles.metadataLabel}>{key}:</span>
                <span className={styles.metadataValue}>{value}</span>
              </div>
            ))}
        </div>
      </div>
    );
  }

  // Determine the grid class based on the number of columns
  let gridClass;

  switch (columns.length) {
    case 1:
      gridClass = null;
      break;
    case 2:
      gridClass = styles.twoColumnGrid;
      break;
    case 3:
      gridClass = styles.threeColumnGrid;
      break;
    case 4:
      gridClass = styles.fourColumnGrid;
      break;
    default:
      gridClass = styles.fiveColumnGrid;
      break;
  }

  return (
    <div className={detailsClassName}>
      <FullSizeImageDisplay isContentBlurred={isContentBlurred} item={item} onToggleBlur={onToggleBlur} />
      {gridClass ? <div className={gridClass}>{columns}</div> : columns}
    </div>
  );
}
